"use client";

import * as React from "react";
import { MessageSquarePlus, X } from "lucide-react";
import { Button } from "../ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../ui/sidebar";
import { ThemeToggle } from "../../theme/theme-toggle";
import {
  ChatConversationList,
  type ChatConversationItem,
  type ChatShellLinkComponent,
} from "./chat-conversation-list";

export interface ChatSidebarProps {
  /** Expanded lockup for the 52px logo slot. */
  logo?: React.ReactNode;
  /** Collapsed mark for the 48px icon rail. */
  logoIcon?: React.ReactNode;
  conversations: ChatConversationItem[];
  onNewChat: () => void;
  onDeleteConversation?: (id: string) => void;
  LinkComponent?: ChatShellLinkComponent;
}

/** The New chat outline row: the variant supplies the 1px --sidebar-border ring, but also a
 *  `bg-background` fill the artboards draw transparent. */
const OUTLINE_ROW = "h-9 rounded-lg bg-transparent";

/**
 * The chat sidebar's four regions: logo slot, New chat, the conversation list, and the footer.
 * Mirrors the shape of components/app-sidebar.tsx (logo ReactNode, LinkComponent, footer) without
 * extending it — that one is a nav sidebar, not a conversation list.
 */
export function ChatSidebar({
  logo,
  logoIcon,
  conversations,
  onNewChat,
  onDeleteConversation,
  LinkComponent,
}: ChatSidebarProps) {
  // The Sheet branch hides its own close control (ui/sidebar.tsx:190), so the mobile sheet needs
  // ours — and every action that navigates has to close it too, since the persistent layout never
  // remounts the shell to reset openMobile.
  const { isMobile, setOpenMobile } = useSidebar();

  // Closing on the active conversation *changing* is what makes the mobile sheet reliable. The row
  // handler alone is not enough: it reaches the adapter only through Radix Slot merging `onClick`
  // onto `LinkComponent`, and `ChatShellLinkComponent` declares `{ to, className, children }` as the
  // whole contract — the kit's own TestLink destructures exactly those three and drops the handler.
  // help-web works only because its ChatLink spreads every prop onto react-router's Link behind a
  // type assertion; an adapter that implements the published type would leave the 288px sheet
  // covering the transcript the user just switched to. This effect owes nothing to the adapter.
  const activeId = conversations.find((item) => item.active)?.id ?? null;
  const lastActiveIdRef = React.useRef(activeId);
  React.useEffect(() => {
    if (lastActiveIdRef.current === activeId) return; // mount, and re-renders that did not switch
    lastActiveIdRef.current = activeId;
    if (isMobile) setOpenMobile(false);
  }, [activeId, isMobile, setOpenMobile]);

  return (
    <Sidebar collapsible="icon" className="border-sidebar-border">
      <SidebarHeader className="h-13 flex-row items-center justify-between gap-2 border-b px-4 py-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
        {logo ? (
          <span className="flex min-w-0 items-center group-data-[collapsible=icon]:hidden">
            {logo}
          </span>
        ) : null}
        {logoIcon ? (
          <span className="hidden items-center group-data-[collapsible=icon]:flex">{logoIcon}</span>
        ) : null}
        {isMobile ? (
          <Button
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            aria-label="Close sidebar"
            onClick={() => setOpenMobile(false)}
          >
            <X />
          </Button>
        ) : null}
      </SidebarHeader>

      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                variant="outline"
                tooltip="New chat"
                className={OUTLINE_ROW}
                onClick={() => {
                  // Below 768px the sidebar is a controlled Sheet and ChatLayout is persistent, so
                  // navigating to `/` does not remount the shell and openMobile stays true — the
                  // 288px sheet would keep covering the new-chat composer the user just asked for.
                  if (isMobile) setOpenMobile(false);
                  onNewChat();
                }}
              >
                <MessageSquarePlus />
                <span className="font-medium">New chat</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarContent>
        <SidebarGroup className="gap-0.5">
          <SidebarGroupLabel className="h-7 uppercase tracking-[0.04em] text-muted-foreground">
            {conversations.length > 0 ? "Recent" : "Chats"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <ChatConversationList
              conversations={conversations}
              onDeleteConversation={onDeleteConversation}
              LinkComponent={LinkComponent}
            />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="gap-1.5 border-t">
        <div className="flex h-9 items-center justify-between gap-2 pr-1 pl-2.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <span className="text-sm group-data-[collapsible=icon]:hidden">Appearance</span>
          {/* The rail shrinks the shared 36px toggle to the artboards' 32px from the outside —
              theme/theme-toggle.tsx itself is untouched. */}
          <span className="flex shrink-0 items-center group-data-[collapsible=icon]:[&>button]:size-8">
            <ThemeToggle />
          </span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
